import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AiFillStar, AiOutlineStar } from "react-icons/ai";
import { useContext } from "react";
import { UrlContext } from "../contexts/UrlContext";

export default function FeedbackWidget({ name, email, topClass = "top-2" }) {
  const [showTab, setShowTab] = useState(true); // reappears on reload
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    rating: 0, // 1-5
    experience: "",
    feature: "",
    recommend: null, // 0-10
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { API_URL } = useContext(UrlContext);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}send-to-teams`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // New structured payload
          name: name || null,
          email: email || null,
          rating: form.rating,
          experience: form.experience,
          feature: form.feature,
          recommend: form.recommend,
        }),
      });
      if (response.ok) {
        setSubmitted(true);
        setForm({ rating: 0, experience: "", feature: "", recommend: null });
      } else {
        alert("Error sending message to Teams.");
      }
    } catch (err) {
      console.error(err);
      alert("Error submitting form.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Top-center feedback tab */}
      <AnimatePresence>
        {showTab && !open && (
          <motion.div
            key="feedback-tab"
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -40, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className={`fixed left-1/2 -translate-x-1/2 z-[1000] ${topClass}`}
          >
            <div className="flex items-center gap-2 bg-[#365561] text-white px-4 py-2 rounded-full shadow-lg border border-white/20">
              <button
                onClick={() => setOpen(true)}
                className="text-sm font-medium hover:opacity-90 focus:outline-none"
              >
                We value your feedback
              </button>
              <button
                aria-label="Close feedback tab"
                onClick={() => setShowTab(false)}
                className="ml-1 text-white/80 hover:text-white focus:outline-none"
                title="Dismiss"
              >
                ✕
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="feedback-backdrop"
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              key="feedback-modal"
              className="bg-white rounded-2xl p-6 w-full max-w-2xl shadow-xl"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              {!submitted ? (
                <>
                  <h2 className="text-2xl font-semibold mb-2 text-center">
                    Help us improve
                  </h2>
                  <p className="text-sm text-gray-500 mb-6 text-center">
                    Please share your thoughts. It takes less than a minute.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* 1. Rating */}
                    <div>
                      <div className="flex items-baseline gap-2">
                        <h3 className="font-semibold">
                          1. Rate your overall experience ✨
                        </h3>
                        <span className="text-red-500">*</span>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">
                        On a scale from 1 to 5, how satisfied are you with your
                        overall experience using our platform?
                      </p>
                      <div className="flex items-center gap-2">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() =>
                              setForm((f) => ({ ...f, rating: n }))
                            }
                            className="text-2xl text-amber-400 hover:scale-105 transition"
                            aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`}
                          >
                            {form.rating >= n ? (
                              <AiFillStar />
                            ) : (
                              <AiOutlineStar />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* 2. Experience textarea */}
                    <div>
                      <div className="flex items-baseline gap-2">
                        <h3 className="font-semibold">
                          2. Tell us about your experience 💬
                        </h3>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">
                        Please describe your experience using our platform. What
                        did you enjoy most, and what could be improved?
                      </p>
                      <textarea
                        name="experience"
                        placeholder="Write your response"
                        value={form.experience}
                        onChange={handleChange}
                        className="border border-gray-300 p-3 w-full rounded-md focus:outline-none focus:ring-2 focus:ring-[#365561] min-h-[96px]"
                      />
                    </div>

                    {/* 3. Feature request */}
                    <div>
                      <h3 className="font-semibold">
                        3. What feature would you like us to add or improve? 💡
                      </h3>
                      <p className="text-sm text-gray-500 mb-2">
                        If you could suggest one new feature or improvement,
                        what would it be?
                      </p>
                      <textarea
                        name="feature"
                        placeholder="Write your response"
                        value={form.feature}
                        onChange={handleChange}
                        className="border border-gray-300 p-3 w-full rounded-md focus:outline-none focus:ring-2 focus:ring-teal-600 min-h-[96px]"
                      />
                    </div>

                    {/* 4. Recommend scale 0-10 */}
                    <div>
                      <h3 className="font-semibold">
                        4. How likely are you to recommend us to others? 📊
                      </h3>
                      <p className="text-sm text-gray-500 mb-3">
                        On a scale from 0 to 10, how likely are you to recommend
                        our platform to a friend or colleague?
                        <br /> (0 = Not likely, 10 = Extremely likely)
                      </p>
                      <div className="grid grid-cols-11 gap-2 sm:flex sm:flex-wrap">
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() =>
                              setForm((f) => ({ ...f, recommend: n }))
                            }
                            className={`px-3 py-2 rounded-md text-sm border transition ${
                              form.recommend === n
                                ? "bg-[#365561] text-white border-[#2b434c]"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-between pt-2">
                      <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading || form.rating === 0}
                        className={`px-5 py-2 rounded-md text-white ${
                          loading || form.rating === 0
                            ? "bg-[#365561]/50 cursor-not-allowed"
                            : "bg-[#365561] hover:bg-[#2b434c]"
                        }`}
                      >
                        {loading ? "Sending..." : "Submit"}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex flex-col items-center text-center py-8">
                  <div className="w-14 h-14 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-8 h-8"
                    >
                      <path
                        fillRule="evenodd"
                        d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-2.59a.75.75 0 1 0-1.22-.86l-3.67 5.22-1.86-1.86a.75.75 0 1 0-1.06 1.06l2.5 2.5a.75.75 0 0 0 1.16-.1l4.21-6Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">
                    Thank you for your feedback!
                  </h3>
                  <p className="text-gray-600 mb-6 max-w-md">
                    We appreciate you taking the time to help us improve. Your
                    responses have been recorded.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setOpen(false);
                      setShowTab(false);
                    }}
                    className="bg-teal-600 hover:bg-teal-700 text-white px-5 py-2 rounded-md"
                  >
                    Close
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
